/**
 * Recurring > New schedule (tracker item 29).
 *
 * Pick customer > property > a DRAFT template job at that property, then the
 * pattern (frequency, interval, weekdays / day of month / nth weekday), the
 * start and optional end date, the billing (per visit, or a flat contract
 * with its amount and billing period, tracker item 34) and notes. Property
 * and template cannot be changed after creation (the backend PATCH ignores
 * them); billing can, on the schedule page.
 *
 * Query string prefill: customerId, propertyId, templateJobId (from the
 * customer page and "Make recurring" on a draft job).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { JobTimeFields, TimeFieldValues, timeFieldsFromJob, timeFieldsPatch } from '../components/JobTimeFields';
import { formatDuration, formatTime } from './dispatchUi';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createAPIClient, ICustomersApi, IJobsApi, IPropertiesApi, IRecurringJobsApi, IRecurringPresetsApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/apiError';
import { canManageRecurring } from '../utils/permissions';
import { RECURRENCE_ORDINALS, WEEKDAY_NAMES, WEEKDAY_SHORT, describeRecurrence } from '../utils/recurrence';
import { formatJobAssignee, formatJobDate, formatJobMoney } from '../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { localDateOnly } from './paymentUi';
import { RecurringBillingFields } from './RecurringBillingFields';
import {
  BillingForm,
  FREQUENCY_LABELS,
  INTERVAL_UNITS,
  RECURRING_PATH,
  RecurrenceForm,
  billingFormErrors,
  buildCreateScheduleRequest,
  defaultBillingForm,
  buildFrequencyDetails,
  defaultRecurrenceForm,
  firstPatternDate,
  parseInterval,
  recurrenceFormErrors,
  recurringDetailPath,
  sampleDates,
  templateJobIssue,
} from './recurringUi';
import { Toast } from '../components/Toast';
import { billingFromPreset, describePreset, recurrenceFromPreset } from './recurringPresetUi';

const ORDINAL_LABELS: Record<number, string> = { 1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', [-1]: 'Last' };

type Api = ICustomersApi & IPropertiesApi & IJobsApi & IRecurringJobsApi & IRecurringPresetsApi;

export function RecurringCreatePage() {
  const api = useMemo<Api>(() => createAPIClient(), []);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const canManage = canManageRecurring(user);
  const initialCustomerId = params.get('customerId') || '';
  const initialPropertyId = params.get('propertyId') || '';
  const initialTemplateId = params.get('templateJobId') || '';

  const [customers, setCustomers] = useState<types.Customer[]>([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPages, setCustomerPages] = useState(1);
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const [properties, setProperties] = useState<types.Property[] | null>(null);
  const [propertyId, setPropertyId] = useState(initialPropertyId);
  const [drafts, setDrafts] = useState<types.Job[] | null>(null);
  const [templateJobId, setTemplateJobId] = useState(initialTemplateId);
  const [form, setForm] = useState<RecurrenceForm>(() => defaultRecurrenceForm(localDateOnly()));
  const [notes, setNotes] = useState('');
  const [billing, setBilling] = useState<BillingForm>(defaultBillingForm);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startSeeded, setStartSeeded] = useState(false);
  /** Active recurring presets (tracker item 76); the select is left out when there are none or they cannot be loaded */
  const [presets, setPresets] = useState<types.RecurringPreset[]>([]);
  const [presetId, setPresetId] = useState('');
  /** The visits' time fields, edited on the template job (tracker item 40) */
  const [visitTime, setVisitTime] = useState<TimeFieldValues>(timeFieldsFromJob(null));

  // Customers (paged, 50 at a time), plus the prefilled one if it is not on page 1
  useEffect(() => {
    if (!canManage) { setLoading(false); return; }
    let active = true;
    Promise.allSettled([
      api.listCustomers(customerPage, 50),
      initialCustomerId && customerPage === 1 ? api.getCustomer(initialCustomerId) : Promise.resolve(null),
    ]).then(([listResult, selectedResult]) => {
      if (!active) return;
      if (listResult.status === 'fulfilled') {
        setCustomers((previous) => {
          const merged = customerPage === 1 ? listResult.value.data : [...previous, ...listResult.value.data.filter((c) => !previous.some((p) => p.id === c.id))];
          const selected = selectedResult.status === 'fulfilled' ? selectedResult.value : null;
          return selected && !merged.some((c) => c.id === selected.id) ? [selected, ...merged] : merged;
        });
        setCustomerPages(Math.max(1, listResult.value.pagination.pages));
      } else {
        setError(getErrorMessage(listResult.reason, 'Failed to load customers'));
      }
      setLoading(false);
    });
    return () => { active = false; };
  }, [api, canManage, customerPage, initialCustomerId]);

  // Presets to start from: a convenience, so a failure just hides the select
  useEffect(() => {
    if (!canManage) return;
    let active = true;
    (async () => {
      try {
        const list = await api.listRecurringPresets(true);
        if (active) setPresets(list);
      } catch {
        if (active) setPresets([]);
      }
    })();
    return () => { active = false; };
  }, [api, canManage]);

  // Properties of the chosen customer (the backend returns them all for a customerId)
  useEffect(() => {
    if (!customerId) { setProperties(null); return; }
    let active = true;
    setProperties(null);
    api.listProperties(customerId)
      .then((result) => { if (active) setProperties(result.data); })
      .catch((cause) => { if (active) { setProperties([]); setError(getErrorMessage(cause, 'Failed to load properties')); } });
    return () => { active = false; };
  }, [api, customerId]);

  // DRAFT jobs at the chosen property: the template candidates
  useEffect(() => {
    if (!propertyId) { setDrafts(null); return; }
    let active = true;
    setDrafts(null);
    api.listJobs(1, 100, { propertyId, status: types.JobStatus.DRAFT })
      .then((result) => { if (active) setDrafts(result.data.filter((job) => job.propertyId === propertyId)); })
      .catch((cause) => { if (active) { setDrafts([]); setError(getErrorMessage(cause, 'Failed to load draft jobs')); } });
    return () => { active = false; };
  }, [api, propertyId]);

  const template = drafts?.find((job) => job.id === templateJobId) ?? null;

  // The visit time editor starts from the chosen template's values
  useEffect(() => { setVisitTime(timeFieldsFromJob(template)); }, [template]);

  // Seed the start date (and pattern) from a prefilled template's scheduled date, once
  useEffect(() => {
    if (startSeeded || !template || template.id !== initialTemplateId) return;
    setStartSeeded(true);
    const date = template.scheduledDate?.slice(0, 10);
    if (date && date >= localDateOnly()) setForm(defaultRecurrenceForm(date));
  }, [template, startSeeded, initialTemplateId]);

  function changeCustomer(id: string) {
    setError(null);
    setCustomerId(id);
    setPropertyId('');
    setTemplateJobId('');
    setDrafts(null);
  }

  function changeProperty(id: string) {
    setError(null);
    setPropertyId(id);
    setTemplateJobId('');
  }

  /** Pre-fill the pattern and billing terms from a preset. Everything stays editable and no link is kept. */
  function choosePreset(id: string) {
    setPresetId(id);
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    setForm((current) => recurrenceFromPreset(preset, current));
    setBilling(billingFromPreset(preset));
    setNotes((current) => current.trim() ? current : preset.notes ?? '');
  }

  function update(patch: Partial<RecurrenceForm>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function toggleDay(day: number) {
    setForm((current) => ({
      ...current,
      daysOfWeek: current.daysOfWeek.includes(day) ? current.daysOfWeek.filter((d) => d !== day) : [...current.daysOfWeek, day],
    }));
  }

  const formErrors = recurrenceFormErrors(form);
  const firstDate = firstPatternDate(form);
  const property = properties?.find((p) => p.id === propertyId) ?? null;
  const templateIssue = template ? templateJobIssue(template) : null;
  const pickErrors: string[] = [];
  if (!customerId) pickErrors.push('Choose a customer.');
  else if (!propertyId) pickErrors.push('Choose a property.');
  else if (!templateJobId) pickErrors.push('Choose a draft template job.');
  else if (drafts && !template) pickErrors.push('The chosen template job is not a draft at this property.');
  else if (templateIssue) pickErrors.push(`The template job ${templateIssue}.`);
  const billingErrors = billingFormErrors(billing);
  const valid = pickErrors.length === 0 && Object.keys(formErrors).length === 0 && Object.keys(billingErrors).length === 0;
  const today = localDateOnly();
  const preview = sampleDates(form);
  const interval = parseInterval(form.interval);
  const unit = INTERVAL_UNITS[form.frequency];
  const show = (field: keyof typeof formErrors) => (touched ? formErrors[field] : undefined) ?? null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!valid || saving || !template) {
      if (pickErrors.length) setError(pickErrors[0]);
      return;
    }
    const timePatch = timeFieldsPatch(visitTime, template, template.scheduledDate ?? '');
    if (typeof timePatch === 'string') { setError(timePatch); return; }
    setSaving(true);
    setError(null);
    try {
      // Generated visits copy the template's time, duration and window: save
      // any edits to the template first
      if (Object.keys(timePatch).length > 0) await api.updateJob(template.id, timePatch);
      const created = await api.createRecurringSchedule(buildCreateScheduleRequest(form, propertyId, template.id, notes, billing));
      navigate(recurringDetailPath(created.id), { replace: true });
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not create the schedule'));
      setSaving(false);
    }
  }

  if (!canManage) {
    return <main className="max-w-4xl mx-auto p-4 sm:p-6"><p role="alert">You do not have permission to create recurring schedules.</p></main>;
  }

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <Link to={RECURRING_PATH} className="inline-block py-2 sm:py-0 text-sm text-blue-700 hover:underline">← Recurring services</Link>
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mt-2 mb-1">New recurring schedule</h1>
      <p className="text-gray-600 mb-6 text-sm">
        Each visit copies the template job's work items and assignee. The property and template cannot be changed after the schedule
        is created; to change them, cancel the schedule and create a new one. Billing can be changed later on the schedule page.
      </p>
      {error && <Toast kind="error">{error}</Toast>}

      {loading ? <p role="status">Loading customers…</p> : (
        <form onSubmit={submit} noValidate className="bg-white shadow rounded-lg p-4 sm:p-7 space-y-8">
          <fieldset className="space-y-4" disabled={saving}>
            <legend className="text-lg font-semibold text-gray-900">What and where</legend>
            <div>
              <label htmlFor="recurring-customer" className={labelClass}>Customer *</label>
              <select id="recurring-customer" className={inputClass} value={customerId} onChange={(event) => changeCustomer(event.target.value)}>
                <option value="">Select customer</option>
                {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
              </select>
              {customerPage < customerPages && (
                <button type="button" onClick={() => setCustomerPage((p) => p + 1)} className="mt-2 py-2 sm:py-0 text-sm text-blue-700 hover:underline">Load more customers</button>
              )}
            </div>

            <div>
              <label htmlFor="recurring-property" className={labelClass}>Property *</label>
              <select id="recurring-property" className={inputClass} value={propertyId} onChange={(event) => changeProperty(event.target.value)} disabled={!customerId || properties === null}>
                <option value="">{customerId && properties === null ? 'Loading properties…' : 'Select property'}</option>
                {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.address}, {p.city}</option>)}
              </select>
              {customerId && properties?.length === 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  This customer has no properties. <Link to={`/customers/${customerId}/properties/new`} className="text-blue-700 underline">Add a property</Link>.
                </p>
              )}
            </div>

            <div>
              <span className={labelClass} id="recurring-template-label">Template job (a draft at this property) *</span>
              {!propertyId ? (
                <p className="text-sm text-gray-500">Choose a property to see its draft jobs.</p>
              ) : drafts === null ? (
                <p className="text-sm text-gray-500">Loading draft jobs…</p>
              ) : drafts.length === 0 ? (
                <p className="text-sm text-gray-600 rounded-md border border-dashed border-gray-300 p-4">
                  There are no draft jobs at {property ? property.address : 'this property'}. A schedule repeats a draft job: {' '}
                  <Link to={`/jobs/new?customerId=${encodeURIComponent(customerId)}`} className="text-blue-700 underline">create a draft job</Link>
                  {' '}with an assignee and the work items for each visit, then come back.
                </p>
              ) : (
                <div role="radiogroup" aria-labelledby="recurring-template-label" className="space-y-2">
                  {drafts.map((job) => {
                    const issue = templateJobIssue(job);
                    return (
                      <label key={job.id} className={`flex items-start gap-3 rounded-md border p-3 ${issue ? 'border-gray-200 bg-gray-50 text-gray-500' : templateJobId === job.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                        <input
                          type="radio"
                          name="recurring-template"
                          value={job.id}
                          checked={templateJobId === job.id}
                          disabled={!!issue}
                          onChange={() => { setError(null); setTemplateJobId(job.id); }}
                          className="mt-1"
                        />
                        <span className="text-sm min-w-0 break-words">
                          <span className="font-semibold">
                            {job.lineItems.length} work {job.lineItems.length === 1 ? 'item' : 'items'} · {formatJobMoney(job.total)}
                          </span>
                          <span className="block">
                            Assignee: {formatJobAssignee(job.assignedToUser)} · Scheduled: {formatJobDate(job.scheduledDate)}
                            {' · '}<Link to={`/jobs/${job.id}`} className="text-blue-700 underline" target="_blank" rel="noopener noreferrer">open job</Link>
                          </span>
                          {job.lineItems.length > 0 && (
                            <span className="block text-xs text-gray-500">{job.lineItems.map((line) => line.item.name).join(', ')}</span>
                          )}
                          <span className="block text-xs text-gray-500">
                            Visit time: {job.scheduledStartTime ? formatTime(job.scheduledStartTime) : 'no time set'} · {formatDuration(job.estimatedDurationMinutes ?? job.suggestedDurationMinutes ?? 0)}
                          </span>
                          {issue && <span className="block text-xs text-amber-800">Cannot be a template: {issue}.</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </fieldset>

          {template && (
            <fieldset className="space-y-4" disabled={saving} aria-label="Visit time">
              <legend className="text-lg font-semibold text-gray-900">Visit time</legend>
              <p className="text-sm text-gray-600">
                Every visit is generated with this start time, duration and arrival window (saved on the template job).
                {!template.scheduledDate && ' Give the template job a date to set a start time.'}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <JobTimeFields idPrefix="recurring-visit" values={visitTime} onChange={setVisitTime} disabled={saving}
                  hasDate={Boolean(template.scheduledDate)} job={template} />
              </div>
            </fieldset>
          )}

          <fieldset className="space-y-4" disabled={saving}>
            <legend className="text-lg font-semibold text-gray-900">How often</legend>
            {presets.length > 0 && (
              <div>
                <label htmlFor="recurring-preset" className={labelClass}>Start from a preset</label>
                <select id="recurring-preset" className={inputClass} value={presetId} onChange={(event) => choosePreset(event.target.value)}>
                  <option value="">None (set it up by hand)</option>
                  {presets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name} ({describePreset(preset)})</option>)}
                </select>
                <p className="mt-1 text-xs text-gray-500">Fills in the pattern and billing terms below. You can still change anything; the schedule is not linked to the preset.</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="recurring-frequency" className={labelClass}>Frequency *</label>
                <select id="recurring-frequency" className={inputClass} value={form.frequency} onChange={(event) => update({ frequency: event.target.value as types.RecurringFrequency })}>
                  {types.RECURRING_FREQUENCIES.map((value) => <option key={value} value={value}>{FREQUENCY_LABELS[value]}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="recurring-interval" className={labelClass}>Repeat every</label>
                <div className="flex items-center gap-2">
                  <input
                    id="recurring-interval"
                    type="number"
                    min={1}
                    max={99}
                    step={1}
                    className={`${inputClass} max-w-[7rem]`}
                    value={form.interval}
                    onChange={(event) => update({ interval: event.target.value })}
                    aria-invalid={show('interval') ? true : undefined}
                  />
                  <span className="text-sm text-gray-700">{interval === 1 ? unit[0] : unit[1]}</span>
                </div>
                {show('interval') && <p className="mt-1 text-sm text-red-700">{show('interval')}</p>}
              </div>
            </div>

            {form.frequency === 'WEEKLY' && (
              <div role="group" aria-labelledby="recurring-days-label">
                <span id="recurring-days-label" className={labelClass}>On these days *</span>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAY_SHORT.map((label, day) => (
                    <label key={label} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2.5 sm:py-1.5 text-sm ${form.daysOfWeek.includes(day) ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                      <input type="checkbox" checked={form.daysOfWeek.includes(day)} onChange={() => toggleDay(day)} aria-label={WEEKDAY_NAMES[day]} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {form.frequency === 'MONTHLY' && (
              <div role="radiogroup" aria-label="Monthly pattern" className="space-y-3">
                <label className="flex flex-wrap items-center gap-2 text-sm">
                  <input type="radio" name="monthly-mode" checked={form.monthlyMode === 'DAY_OF_MONTH'} onChange={() => update({ monthlyMode: 'DAY_OF_MONTH' })} />
                  On day
                  <input
                    type="number"
                    min={1}
                    max={31}
                    aria-label="Day of the month"
                    className={`${inputClass} max-w-[6rem]`}
                    value={form.dayOfMonth}
                    disabled={form.monthlyMode !== 'DAY_OF_MONTH'}
                    onChange={(event) => update({ dayOfMonth: event.target.value })}
                  />
                  of the month <span className="text-gray-500">(shorter months use their last day)</span>
                </label>
                <label className="flex flex-wrap items-center gap-2 text-sm">
                  <input type="radio" name="monthly-mode" checked={form.monthlyMode === 'NTH_WEEKDAY'} onChange={() => update({ monthlyMode: 'NTH_WEEKDAY' })} />
                  On the
                  <select aria-label="Which week" className={`${inputClass} max-w-[8rem]`} value={form.ordinal} disabled={form.monthlyMode !== 'NTH_WEEKDAY'} onChange={(event) => update({ ordinal: event.target.value })}>
                    {RECURRENCE_ORDINALS.map((value) => <option key={value} value={String(value)}>{ORDINAL_LABELS[value]}</option>)}
                  </select>
                  <select aria-label="Day of the week" className={`${inputClass} max-w-[10rem]`} value={form.weekday} disabled={form.monthlyMode !== 'NTH_WEEKDAY'} onChange={(event) => update({ weekday: event.target.value })}>
                    {WEEKDAY_NAMES.map((name, day) => <option key={name} value={String(day)}>{name}</option>)}
                  </select>
                  of the month
                </label>
              </div>
            )}
            {show('pattern') && <p role="alert" className="text-sm text-red-700">{show('pattern')}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="recurring-start" className={labelClass}>Start date *</label>
                <input id="recurring-start" type="date" className={inputClass} value={form.startDate} onChange={(event) => update({ startDate: event.target.value })} aria-invalid={show('startDate') ? true : undefined} />
                {formErrors.startDate && firstDate && firstDate !== form.startDate ? (
                  <p className="mt-1 text-sm text-red-700">
                    {formErrors.startDate}{' '}
                    <button type="button" className="font-semibold underline" onClick={() => update({ startDate: firstDate })}>Use {formatJobDate(firstDate)}</button>
                  </p>
                ) : show('startDate') && <p className="mt-1 text-sm text-red-700">{show('startDate')}</p>}
              </div>
              <div>
                <label htmlFor="recurring-end" className={labelClass}>End date (optional)</label>
                <input id="recurring-end" type="date" className={inputClass} value={form.endDate} min={form.startDate || undefined} onChange={(event) => update({ endDate: event.target.value })} aria-invalid={show('endDate') ? true : undefined} />
                {show('endDate') && <p className="mt-1 text-sm text-red-700">{show('endDate')}</p>}
              </div>
            </div>
            {form.startDate && form.startDate < today && !formErrors.startDate && (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                The start date is in the past. The first <strong>Generate now</strong> creates a job for every past date in the pattern
                (up to 100 per run).
              </p>
            )}

            <div className="rounded-md bg-gray-50 p-4 text-sm" aria-live="polite">
              <p className="font-semibold text-gray-900">
                {!formErrors.pattern
                  ? describeRecurrence(form.frequency, Number.isInteger(interval) ? interval : 1, buildFrequencyDetails(form))
                  : 'Pattern incomplete'}
              </p>
              {preview.length > 0 && (
                <p className="mt-1 text-gray-700">First visits: {preview.map((date) => formatJobDate(date)).join(', ')}{form.endDate ? '' : ' …'}</p>
              )}
            </div>
          </fieldset>

          <fieldset className="space-y-4" disabled={saving}>
            <legend className="text-lg font-semibold text-gray-900">Billing and notes</legend>
            <RecurringBillingFields
              idPrefix="recurring-billing"
              form={billing}
              errors={touched ? billingErrors : {}}
              onChange={(patch) => setBilling((current) => ({ ...current, ...patch }))}
            />
            <div>
              <label htmlFor="recurring-notes" className={labelClass}>Schedule notes</label>
              <textarea id="recurring-notes" className={inputClass} rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
              <p className="mt-1 text-xs text-gray-500">For the office only. Generated jobs copy the template job's notes, not these.</p>
            </div>
          </fieldset>

          <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-3 border-t pt-5">
            <Link to={RECURRING_PATH} className={`${secondaryButtonClass} text-center py-2.5 sm:py-2`}>Cancel</Link>
            <button type="submit" className={`${primaryButtonClass} py-2.5 sm:py-2`} disabled={saving || (touched && !valid)}>
              {saving ? 'Creating…' : 'Create schedule'}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
